<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const props = defineProps<{
  umsoknType: number
  umsoknId: string | number
  filespace: number
  disable?: boolean
  labelText?: string
}>()

const emit = defineEmits(['uploaded', 'removed'])

const files = ref<{ id: number | null; fileName: string; size: number }[]>([])
const errorMessage = ref('')
const error = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const isLoading = ref(false)

const { uploadFile, setFiles, deleteFile: deleteFileApi } = useUmsokn()

const umsoknItem = {
  id: props.umsoknId,
  type: props.umsoknType,
  fileSpace: props.filespace
}

async function fetchFiles() {
  try {
    const result = await setFiles(umsoknItem)
    files.value = result?.objects ?? []
  } catch {
    files.value = []
  }
}

function chooseFiles() {
  if (!isLoading.value) fileInput.value?.click()
}

async function handleFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  if (!input.files?.length) return

  const file = input.files[0]
  const allowedExt = ['pdf', 'doc', 'docx', 'png', 'jpg']
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''

  if (!allowedExt.includes(ext)) {
    error.value = true
    errorMessage.value = 'Ógilt skráarsnið'
    return
  }
  if (file.size > 10 * 1024 * 1024) {
    error.value = true
    errorMessage.value = 'Skráin er of stór (hámark 10MB)'
    return
  }

  isLoading.value = true
  try {
    await uploadFile(file, umsoknItem)
    await fetchFiles()
    error.value = false
    emit('uploaded')
  } catch {
    error.value = true
    errorMessage.value = 'Villa við að hlaða upp skránni'
  } finally {
    isLoading.value = false
    input.value = ''
  }
}

async function removeFile(id: number) {
  isLoading.value = true
  try {
    await deleteFileApi(id)
    files.value = files.value.filter(f => f.id !== id)
    emit('removed')
  } catch {
    error.value = true
    errorMessage.value = 'Ekki tókst að eyða skrá'
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchFiles)
</script>

<template>
  <div class="file-upload">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-blur-overlay">
      <v-progress-circular indeterminate size="48" color="primary" class="blur-spinner" />
    </div>

    <!-- Upload UI -->
    <div
      v-if="!disable"
      class="forms__file__container"
      @click="chooseFiles"
    >
      <div class="element__center">
        <img src="@/assets/icons/file-upload.svg" />
        <label>{{ $t('file_input_subtext') }}</label>
        <p style="font-size:14px; width:90%">{{ labelText }}</p>
      </div>
    </div>

    <div v-else>
      <div class="element__center">
        <label>{{ $t('file_input_attachment') }}</label>
      </div>
    </div>

    <input
      type="file"
      ref="fileInput"
      class="hidden"
      :disabled="disable"
      @change="handleFileChange"
    />

    <!-- Error message -->
    <v-alert v-if="error" type="error" dismissible>
      {{ errorMessage }}
    </v-alert>

    <!-- File list -->
    <div
      v-for="file in files"
      :key="file.id ?? file.fileName"
      class="forms__file__information"
    >
      <div class="forms__file__logo">
        <img src="@/assets/icons/file-logo.svg" />
        <span>
          <span class="forms__file__name">{{ file.fileName }}</span>
          <br />
          {{ (file.size / 1024).toFixed(1) }} KB
        </span>
      </div>
      <div v-if="!disable && file.id" class="forms__warning__wrapper">
        <button @click="removeFile(file.id)">
          <img src="@/assets/icons/Close.svg" class="forms__warning__close" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.forms__file__container {
  cursor: pointer;
  padding: 16px;
  border: 2px dashed #ccc;
  text-align: center;
  margin-bottom: 16px;
}

.forms__file__information {
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  height: 80px;
  margin-bottom: 12px;
  padding: 0 12px;
}

.forms__file__logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.forms__file__name {
  font-weight: bold;
}

.hidden {
  display: none;
}

.file-upload {
  position: relative; 
}

.loading-blur-overlay {
  position: absolute;
  inset: 0; 
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  z-index: 5;
}

.blur-spinner {
  filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.2));
}
</style>
